import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { isEmpty } from '../../utils/util';
import { PageSizeSelect, tablePagination } from './table-pagination';
import { Table, Input, Button, InputRef } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { api_product, api_purchase_order } from '../../api/product';
import { api_vendor } from '../../api/vendor';
import { api_member } from '../../api/member';

interface props {
    table: string,
    onSelect: React.Dispatch<React.SetStateAction<object>>
}

// Selectable server-side page sizes for the shared Product Search modal.
export const PRODUCT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 100];
export const PRODUCT_DEFAULT_PAGE_SIZE = 20;

const CommonSearch: React.FC<props> = (prop) => {

    const searchRef = useRef<InputRef>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const latestRequestIdRef = useRef<number>(0);
    const [search, setSearch] = useState("");
    const [data, setData] = useState<any[]>([]);
    const [filterField, setFilterField] = useState<string[]>([]);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Product server-side pagination state
    const [productPage, setProductPage] = useState(1);
    const [productPageSize, setProductPageSize] = useState(PRODUCT_DEFAULT_PAGE_SIZE);
    const [productTotal, setProductTotal] = useState(0);
    const [productTotalPages, setProductTotalPages] = useState(1);

    const isProduct = prop.table.toUpperCase() === 'PRODUCT';

    const onSetColumn = () => { 
        let field: any = []
        if (prop.table.toUpperCase() == 'PRODUCT') {
            field = [
                { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', width: '160px' },
                { title: 'ชื่อสินค้า', dataIndex: 'product_name', key: 'product_name' }
            ]
        } else if (prop.table.toUpperCase() == 'VENDOR') {
            field = [
                { title: 'รหัสผู้ขายส่งสินค้า', dataIndex: 'code', key: 'code' },
                { title: 'ชื่อผู้ขายส่งสินค้า', dataIndex: 'name', key: 'name' }
            ]
        } else if (prop.table.toUpperCase() == 'PURCHASE_ORDER') {
            field = [
                { title: 'เลขที่ใบสั่งซื้อ', dataIndex: 'document_no', key: 'document_no', width: '140px' },
                { title: 'ผู้ขายส่งสินค้า', dataIndex: 'vendor_name', key: 'vendor_name' },
                { title: 'วันที่สั่งซื้อ', dataIndex: 'create_date', key: 'create_date', width: '160px' },
                { title: 'จำนวนรวม', dataIndex: 'quantity', key: 'quantity', width: '90px' },
                { title: 'ต้นทุนรวม', dataIndex: 'cost', key: 'cost', width: '110px' }
            ]
        } else if (prop.table.toUpperCase() == 'MEMBER') {
            field = [
                { title: 'รหัสสมาชิก', dataIndex: 'code', key: 'code' },
                { title: 'ชื่อ', dataIndex: 'firstname', key: 'firstname' },
                { title: 'นามสกุล', dataIndex: 'lastname', key: 'lastname' }
            ]
        } else if (prop.table.toUpperCase() == 'PAUSE') {
            field = [
                { title: 'วันที่', dataIndex: 'date', key: 'date' },
            ]
        }
        
        let col: ColumnsType<any> = [
            ...field,
            ...[{ title: 'การดำเนินการ', dataIndex: 'operation', width: '100px', fixed: 'right', render: (_: any, record: any) =>
                data.length >= 1 ? 
                    prop.table.toUpperCase() == 'PAUSE' ? (
                        <div>
                            <Button className='btn-common' type="primary" onClick={() => {prop.onSelect({...{table: prop.table}, ...record})}}>
                                เลือก
                            </Button>
                            <Button className='btn-common' type="primary" style={{marginLeft: '5px'}} onClick={() => {onRemovePause(record)}}>
                                ลบ
                            </Button>
                        </div>
                    ) :
                    ( 
                        <Button className='btn-common' type="primary" onClick={() => {
                            setSearch('');
                            searchRef.current?.focus();
                            prop.onSelect({...{table: prop.table}, ...record});
                        }}>
                            เลือก
                        </Button>
                    )
                : null
            }]
        ]

        return col;
    }

    const onRemovePause = (record: any) => {
        let pause_data: any = JSON.parse(localStorage.getItem('pause') || "[]");
        pause_data = pause_data.filter((x: any) => { return Object.keys(x) != record['date'][0] });
        if (pause_data.length <= 0) {
            prop.onSelect({table: prop.table, empty: true});
        } else {
            localStorage.setItem('pause', JSON.stringify(pause_data));
            setData(pause_data);
        }
    }

    /**
     * TRUE server-side pagination: the backend returns at most `size` rows.
     * `size` is passed explicitly so a page-size change issues its request with the
     * new value immediately, without waiting for the state update to settle.
     */
    const loadProductPage = useCallback(async (pageNo: number, queryStr: string, size?: number) => {
        const currentReqId = ++latestRequestIdRef.current;
        const trimmed = queryStr.trim();
        const effectiveSize = size || productPageSize;

        setLoading(true);
        setError(null);
        try {
            const res = await api_product('LIST', {
                page: pageNo,
                page_size: effectiveSize,
                search: trimmed || undefined
            });

            // Ignore stale response if user typed something newer
            if (currentReqId !== latestRequestIdRef.current) {
                return;
            }

            if (res && res.items) {
                setData(res.items);
                setProductPage(res.pagination?.page || pageNo);
                setProductPageSize(res.pagination?.page_size || effectiveSize);
                setProductTotal(res.pagination?.total || 0);
                setProductTotalPages(res.pagination?.total_pages || 1);
            } else if (Array.isArray(res)) {
                setData(res);
                setProductPage(1);
                setProductPageSize(effectiveSize);
                setProductTotal(res.length);
                setProductTotalPages(1);
            }
        } catch (err) {
            if (currentReqId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Failed to load products in CommonSearch:', err);
            setError('ค้นหาสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ');
            setData([]);
        } finally {
            if (currentReqId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [productPageSize]);

    const init = async () => {
        setError(null);
        setSearch('');

        if (isProduct) {
            setFilterField(["product_code", "product_name"]);
            setProductPage(1);
            setProductPageSize(PRODUCT_DEFAULT_PAGE_SIZE);
            loadProductPage(1, '', PRODUCT_DEFAULT_PAGE_SIZE);
            searchRef.current?.focus();
            return;
        }

        setLoading(true);
        try {
            let getData: any[] = [];
            if (prop.table.toUpperCase() == 'VENDOR') {
                getData = await api_vendor('LIST', {});
                setFilterField(["code", "name"]);
            } else if (prop.table.toUpperCase() == 'PURCHASE_ORDER') {
                let res = await api_purchase_order('LIST', {});
                if (Array.isArray(res)) {
                    getData = res.filter((po: any) => po.status === 2);
                }
                setFilterField(["document_no", "vendor_name"]);
            } else if (prop.table.toUpperCase() == 'MEMBER') {
                getData = await api_member('GET_BY_CONDITION', {});
                setFilterField(["code", "firstname", "lastname"]);
            } else if (prop.table.toUpperCase() == 'PAUSE') {
                let pause_data: any = JSON.parse(localStorage.getItem('pause') || "[]");
                pause_data.forEach((x: any, index: Number) => {
                    getData.push({key: index, 'date': Object.keys(x)})
                });
                setFilterField([]);
            }

            if (Array.isArray(getData)) {
                setData(getData);
            }
        } catch (err) {
            console.error(`Failed to load ${prop.table}:`, err);
            setError(`โหลดข้อมูลไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ`);
            setData([]);
        } finally {
            setLoading(false);
        }

        searchRef.current?.focus();
    }

    const onSearch = (event: any) => {
        const val = event.target.value;
        setSearch(val);

        if (isProduct) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                loadProductPage(1, val);
            }, 300);
        }
    }

    const onFilter = () => {
        if (isProduct) {
            return data;
        }

        if (isEmpty(search)) {
            return data;
        }

        const sUpper = search.toUpperCase().trim();
        return data.filter((x: any) => {
            return (filterField || []).some((key: string) => {
                const val = x[key];
                return val != null && String(val).toUpperCase().includes(sUpper);
            });
        });
    }

    useEffect(() => {
        if (prop.table) {
            init();
        }
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [prop.table]);

    const getEmptyText = () => {
        if (loading) {
            return <span>กำลังโหลดข้อมูล...</span>;
        }
        if (error) {
            return <span>ค้นหาสินค้าไม่สำเร็จ</span>;
        }
        return <span>ไม่มีข้อมูล</span>;
    };

    return (
        <>
            <div className='filterBox' style={{ marginBottom: '14px' }}>
                { prop.table.toUpperCase() != 'PAUSE' ? <div className='searchBox'>
                    <label>ค้นหา</label><QuestionCircleOutlined rev={undefined} />
                    <Input 
                        ref={searchRef}
                        className='uppercase'
                        placeholder={isProduct ? 'พิมพ์รหัสสินค้า / ชื่อสินค้า เพื่อค้นหา' : 'ค้นหา'}
                        value={search}
                        onChange={onSearch}
                    />
                </div> : null }
            </div>

            {error ? (
                <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#DC2626', fontSize: '13px', fontWeight: 600 }}>⚠️ {error}</span>
                    <Button size="small" type="primary" danger onClick={() => isProduct ? loadProductPage(1, search) : init()} style={{ borderRadius: '6px' }}>
                        ลองใหม่อีกครั้ง (Retry)
                    </Button>
                </div>
            ) : null}

            <Table
                loading={loading}
                columns={onSetColumn()}
                dataSource={onFilter()}
                rowKey={(record: any) => String(record.id ?? record.code ?? record.product_code ?? record.document_no ?? record.key ?? Math.random())}
                locale={{ emptyText: getEmptyText() }}
                scroll={{ x: 600, y: 360 }}
                pagination={isProduct ? {
                    current: productPage,
                    pageSize: productPageSize,
                    total: productTotal,
                    showSizeChanger: true,
                    pageSizeOptions: PRODUCT_PAGE_SIZE_OPTIONS.map(String),
                    selectComponentClass: PageSizeSelect,
                    locale: { items_per_page: '/ หน้า' },
                    showTotal: (total, range) => `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ (หน้า ${productPage}/${productTotalPages})`,
                    onChange: (newPage, newPageSize) => {
                        // Changing page size must reset to page 1 and re-query the server.
                        if (newPageSize && newPageSize !== productPageSize) {
                            setProductPageSize(newPageSize);
                            setProductPage(1);
                            loadProductPage(1, search, newPageSize);
                            return;
                        }
                        loadProductPage(newPage, search);
                    },
                    responsive: true,
                    size: 'small'
                } : {
                    ...tablePagination(pageSize, setPageSize)
                }}
            />
        </>
    );
}

export default CommonSearch;